import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import './EngineeringIntelligence.scss';
import duotone from '../../assets/Images/Engineering/duotone.svg'
import cluster from '../../assets/Images/Engineering/cluster.svg'

const TAB_DATA = [
  'AI & Machine Learning',
  'Internet of Things (IoT)',
  'Software Development',
  'Startup Product Development',
  'Design & UX'
];

const TAB_CONTENT = [
  {
    header: {
      title: 'Turn data into decisions. Automate intelligently.',
      desc: 'We design and deploy AI-driven systems that learn, predict, and optimize business processes in real time.'
    },
    deliverables: [
      { id: 1, title: 'Predictive analytics solutions', icon: duotone },
      { id: 2, title: 'Custom AI model development', icon: duotone },
      { id: 3, title: 'NLP & conversational AI systems', icon: duotone },
      { id: 4, title: 'Computer vision applications', icon: duotone },
      { id: 5, title: 'AI workflow automation pipelines', icon: duotone },
    ],
    outcome: 'Smarter workflows. Accurate forecasting. Competitive advantage through intelligent automation.'
  },

  {
    header: {
      title: 'Connecting the physical and digital world.',
      desc: 'We build secure IoT ecosystems that collect, analyze, and act on real-world device data.'
    },
    deliverables: [
      { id: 1, title: 'IoT device connectivity architecture', icon: duotone },
      { id: 2, title: 'Real-time device monitoring dashboards', icon: duotone },
      { id: 3, title: 'Edge computing implementations', icon: duotone },
      { id: 4, title: 'Industrial IoT data pipelines', icon: duotone },
      { id: 5, title: 'Secure device communication protocols', icon: duotone },
    ],
    outcome: 'Operational visibility. Automated device management. Data-driven infrastructure.'
  },

  {
    header: {
      title: 'Building reliable software that scales.',
      desc: 'Our engineering teams develop high-performance applications and platforms tailored for modern businesses.'
    },
    deliverables: [
      { id: 1, title: 'Full-stack web applications', icon: duotone },
      { id: 2, title: 'Cloud-native backend systems', icon: duotone },
      { id: 3, title: 'API architecture & integrations', icon: duotone },
      { id: 4, title: 'Microservices-based platforms', icon: duotone },
      { id: 5, title: 'Enterprise-grade system architecture', icon: duotone },
    ],
    outcome: 'Scalable systems. Faster deployments. Reliable digital infrastructure.'
  },

  {
    header: {
      title: 'From idea to market-ready product.',
      desc: 'We partner with startups to design, build, and launch scalable digital products quickly and efficiently.'
    },
    deliverables: [
      { id: 1, title: 'MVP design & rapid prototyping', icon: duotone },
      { id: 2, title: 'Startup product architecture', icon: duotone },
      { id: 3, title: 'Scalable backend infrastructure', icon: duotone },
      { id: 4, title: 'Investor-ready product demos', icon: duotone },
      { id: 5, title: 'Growth-ready platform development', icon: duotone },
    ],
    outcome: 'Faster product launches. Reduced development risk. Scalable startup technology.'
  },

  {
    header: {
      title: 'Designing intuitive and impactful experiences.',
      desc: 'We create user-centered digital experiences that combine aesthetics with functionality.'
    },
    deliverables: [
      { id: 1, title: 'User research & product discovery', icon: duotone },
      { id: 2, title: 'Wireframing & interactive prototypes', icon: duotone },
      { id: 3, title: 'Design systems & UI libraries', icon: duotone },
      { id: 4, title: 'Responsive product interfaces', icon: duotone },
      { id: 5, title: 'Usability testing & optimization', icon: duotone },
    ],
    outcome: 'Better user engagement. Seamless product experience. Stronger brand perception.'
  }
];

export default function EngineeringIntelligence() {
  const [activeTab, setActiveTab] = useState(0);

  return (
    <section className="engineering-intelligence">

      {/* Top Header Section */}
      <div className="ei-header-section">
        <div className="cluster-capsule">
          <div className="icon-wrapper">
            <div className="icon-blur" aria-hidden="true"></div>
            <img src={cluster} alt="cluster" className="cluster-icon" />
          </div>
          <span>Cluster B: Finance</span>
        </div>

        <h2 className="ei-heading">
          Engineering Intelligence.
          <br />
          <span className="highlight-box">Building the Future.</span>
        </h2>

        <p className="ei-subheading">
          At Logiqlink, we combine strategy, design, and engineering to build scalable digital ecosystems. From AI-powered platforms to IoT infrastructure and startup-ready products — we don’t just build tech, we build impact.
        </p>
      </div>

      {/* Interactive Tabs Section */}
      <div className="ei-tabs-container">

        {/* Tab Navigation */}
        <div className="ei-tabs-header">
          {TAB_DATA.map((tab, index) => (
            <button
              key={index}
              className={`ei-tab-btn ${activeTab === index ? 'active' : ''}`}
              onClick={() => setActiveTab(index)}
            >
              {tab}
            </button>
          ))}
        </div>

        {/* Tab Content Box */}
        <div className="ei-tab-content-box">
          <AnimatePresence mode="wait">
            <motion.div
              key={activeTab}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.3, ease: 'easeInOut' }}
            >
              <div className="ei-content-header">
                <h3>{TAB_CONTENT[activeTab].header.title}</h3>
                <p>{TAB_CONTENT[activeTab].header.desc}</p>
              </div>

              <div className="ei-deliverables">
                <span className="italic-label">What we deliver:</span>
                <div className="ei-cards-grid">
                  {TAB_CONTENT[activeTab].deliverables.map((item) => (
                    <div key={item.id} className="ei-deliverable-card">
                      <img src={item.icon} alt={item.title} className="card-icon" />
                      <p>{item.title}</p>
                    </div>
                  ))}
                </div>
              </div>

              <div className="ei-outcome mt-5">
                <span className="italic-label">Outcome:</span>
                <p>{TAB_CONTENT[activeTab].outcome}</p>
              </div>

            </motion.div>
          </AnimatePresence>
        </div>

      </div>
    </section>
  );
}